import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CoverageIcon } from "@/components/benefits/v2/typeIcons";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";

/**
 * What the broker is asking carriers to quote for medical.
 *
 * Every field is optional, like the other RFP forms: brokers send open RFPs
 * ("quote your best PPO") as well as spec'd ones, and a blank goes out as
 * "Not specified" rather than blocking the send.
 *
 * networkTypes and fundingTypes are lists, not single choices. A broker
 * routinely asks one carrier for an HMO AND a PPO, or for the same plan priced
 * fully insured AND level funded, so the employer can compare.
 */
export type HealthRFPParameters = {
  networkTypes: string[];
  fundingTypes: string[];
  hsaCompatible: boolean;
  hraCompatible: boolean;
  deductibleIndividual?: number;
  deductibleFamily?: number;
  coinsurance?: string;
  outOfPocketIndividual?: number;
  outOfPocketFamily?: number;
  copayOffice?: number;
  copaySpecialist?: number;
  copayEmergencyRoom?: number;
  rxGeneric?: number;
  rxPreferredBrand?: number;
  rxNonPreferredBrand?: number;
  rxSpecialty?: number;
  employerContributionEmployee?: string;
  employerContributionDependent?: string;
  currentCarrier?: string;
  renewalIncrease?: string;
};

const NETWORK_TYPES = [
  { id: "hmo", label: "HMO" },
  { id: "ppo", label: "PPO" },
  { id: "epo", label: "EPO" },
  { id: "pos", label: "POS" },
];

const FUNDING_TYPES = [
  { id: "fully_insured", label: "Fully insured" },
  { id: "level_funded", label: "Level funded" },
  { id: "self_funded", label: "Self funded" },
];

type HealthRFPFormProps = {
  value: HealthRFPParameters;
  onChange: (value: HealthRFPParameters) => void;
};

export function HealthRFPForm({ value, onChange }: HealthRFPFormProps) {
  const updateField = (field: keyof HealthRFPParameters, newValue: any) => {
    onChange({ ...value, [field]: newValue });
  };

  const toggleIn = (field: "networkTypes" | "fundingTypes", id: string) => {
    const current = value[field] ?? [];
    updateField(
      field,
      current.includes(id) ? current.filter((v) => v !== id) : [...current, id],
    );
  };

  // Blank stays undefined rather than becoming 0: a $0 deductible is a real
  // answer, and the email must be able to tell it from an unanswered question.
  const numberField = (
    field: keyof HealthRFPParameters,
    id: string,
    placeholder: string,
  ) => (
    <div className="flex items-center gap-1">
      <span className="text-sm text-muted-foreground">$</span>
      <Input
        id={id}
        type="number"
        min={0}
        placeholder={placeholder}
        value={(value[field] as number | undefined) ?? ""}
        onChange={(e) =>
          updateField(field, e.target.value === "" ? undefined : Number(e.target.value))
        }
      />
    </div>
  );

  const tickRow = (
    field: "networkTypes" | "fundingTypes",
    options: { id: string; label: string }[],
  ) => (
    <div className="flex flex-wrap gap-x-6 gap-y-2">
      {options.map((option) => (
        <div key={option.id} className="flex items-center space-x-2">
          <Checkbox
            id={`health-${field}-${option.id}`}
            checked={(value[field] ?? []).includes(option.id)}
            onCheckedChange={() => toggleIn(field, option.id)}
          />
          <label htmlFor={`health-${field}-${option.id}`} className="text-sm font-medium leading-none">
            {option.label}
          </label>
        </div>
      ))}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <CoverageIcon planType="health" size="sm" />
          Medical
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Plan design, what the employee pays, and how it is funded.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-3">
          <Label>Network type</Label>
          {tickRow("networkTypes", NETWORK_TYPES)}
          <p className="text-[11px] text-muted-foreground">
            Tick more than one to have each quoted side by side. Leave empty for no preference.
          </p>
        </div>

        <div className="space-y-3">
          <Label>Quote these funding types</Label>
          {tickRow("fundingTypes", FUNDING_TYPES)}
          <p className="text-[11px] text-muted-foreground">
            Level funded and self funded mean the employer pays claims rather than a fixed premium.
            Ticking several asks for each one, so they can be compared.
          </p>
        </div>

        <div className="space-y-3">
          <Label>Must work with</Label>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="health-hsa"
                checked={value.hsaCompatible}
                onCheckedChange={(checked) => updateField("hsaCompatible", checked === true)}
              />
              <label htmlFor="health-hsa" className="text-sm font-medium leading-none">
                HSA
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="health-hra"
                checked={value.hraCompatible}
                onCheckedChange={(checked) => updateField("hraCompatible", checked === true)}
              />
              <label htmlFor="health-hra" className="text-sm font-medium leading-none">
                HRA
              </label>
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground">
            An HSA is a savings account for medical costs and only works with a high-deductible
            plan, so this limits what a carrier can offer. An HRA is funded by the employer.
          </p>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="health-deductible-individual">Deductible, individual</Label>
            {numberField("deductibleIndividual", "health-deductible-individual", "1,000")}
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-deductible-family">Deductible, family</Label>
            {numberField("deductibleFamily", "health-deductible-family", "2,000")}
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-coinsurance">Coinsurance</Label>
            <div className="flex items-center gap-1">
              <Input
                id="health-coinsurance"
                type="number"
                min={0}
                max={100}
                placeholder="80"
                value={value.coinsurance ?? ""}
                onChange={(e) => updateField("coinsurance", e.target.value)}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              The share the plan pays after the deductible.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-oop-individual">Out-of-pocket max, individual</Label>
            {numberField("outOfPocketIndividual", "health-oop-individual", "5,000")}
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-oop-family">Out-of-pocket max, family</Label>
            {numberField("outOfPocketFamily", "health-oop-family", "10,000")}
          </div>
        </div>

        <div className="space-y-3">
          <Label>Copays</Label>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="health-copay-office" className="text-xs font-normal text-muted-foreground">
                Office visit
              </Label>
              {numberField("copayOffice", "health-copay-office", "25")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-copay-specialist" className="text-xs font-normal text-muted-foreground">
                Specialist
              </Label>
              {numberField("copaySpecialist", "health-copay-specialist", "50")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-copay-er" className="text-xs font-normal text-muted-foreground">
                Emergency room
              </Label>
              {numberField("copayEmergencyRoom", "health-copay-er", "250")}
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Label>Prescription copays</Label>
          <div className="grid gap-4 sm:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="health-rx-generic" className="text-xs font-normal text-muted-foreground">
                Generic
              </Label>
              {numberField("rxGeneric", "health-rx-generic", "10")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-rx-preferred" className="text-xs font-normal text-muted-foreground">
                Preferred brand
              </Label>
              {numberField("rxPreferredBrand", "health-rx-preferred", "35")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-rx-non-preferred" className="text-xs font-normal text-muted-foreground">
                Non-preferred
              </Label>
              {numberField("rxNonPreferredBrand", "health-rx-non-preferred", "70")}
            </div>
            <div className="space-y-2">
              <Label htmlFor="health-rx-specialty" className="text-xs font-normal text-muted-foreground">
                Specialty
              </Label>
              {numberField("rxSpecialty", "health-rx-specialty", "150")}
            </div>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="health-contrib-employee">Employer pays, employee</Label>
            <div className="flex items-center gap-1">
              <Input
                id="health-contrib-employee"
                type="number"
                min={0}
                max={100}
                placeholder="80"
                value={value.employerContributionEmployee ?? ""}
                onChange={(e) => updateField("employerContributionEmployee", e.target.value)}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-contrib-dependent">Employer pays, dependents</Label>
            <div className="flex items-center gap-1">
              <Input
                id="health-contrib-dependent"
                type="number"
                min={0}
                max={100}
                placeholder="50"
                value={value.employerContributionDependent ?? ""}
                onChange={(e) => updateField("employerContributionDependent", e.target.value)}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
        </div>

        {/* Two small fields, not a rate table. The real rates go out as the
            attached current plan summary, which the send step already handles;
            retyping a rate sheet into a form nobody validates is how wrong
            numbers reach carriers. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="health-current-carrier">Current carrier</Label>
            <Input
              id="health-current-carrier"
              placeholder="Anthem Blue Cross"
              value={value.currentCarrier ?? ""}
              onChange={(e) => updateField("currentCarrier", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="health-renewal">Renewal increase</Label>
            <div className="flex items-center gap-1">
              <Input
                id="health-renewal"
                type="number"
                placeholder="12"
                value={value.renewalIncrease ?? ""}
                onChange={(e) => updateField("renewalIncrease", e.target.value)}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
            <p className="text-[11px] text-muted-foreground">
              What the current carrier is asking for. Attach the current plan summary to send
              the rates themselves.
            </p>
          </div>
        </div>

      </CardContent>
    </Card>
  );
}
